/**
 * 서버 구성 편집기(별탭) — state 정의 + factory.
 *
 * 저장 원천은 딜의 `deal_item` 서브트리다(server_config 테이블 없음).
 *   신규: 빈 상태로 시작 → 완료 시 POST /deal/:id/server
 *   수정: 그 딜의 서버 사본을 로드 → 완료 시 PUT /deal/server/:serverItemId
 *
 * 편집 중 상태는 브라우저에만 있다. 완료가 유일한 커밋 지점이므로
 * 중간 이탈 시 작업 내용은 사라지고 쓰레기 데이터도 남지 않는다.
 */

import * as api from '../../api/client';
// 딥 경로 import — 배럴(@hcltech/shared)은 auth/jwt(hono/jwt)까지 끌고 와
// 브라우저 번들에서 해석 실패한다. UI 는 항상 서브패스로 가져온다.
import { toSpecCategory, type SpecCategory } from '@hcltech/shared/server-config/spec-category';

/** grid 한 줄. id 가 있으면 기존 자식행(diff 갱신 대상). */
export interface ConfigRow {
  /** 기존 deal_item 자식행 id. 신규 행은 undefined. */
  id?:            string;
  /** 화면 내 임시 키 (신규 행 추적용). */
  key:            string;
  specCategory:   SpecCategory | '';
  optionPart:     string;
  /**
   * 카탈로그 출처 테이블명(cpu/memory/ssd/…). 약식 재계산 시 full row 조회 키.
   * pending(임시등록) 품목은 null.
   */
  catalogSource:  string | null;
  /** 카탈로그 full row — 약식 디스크립션 생성 입력. 미조회면 null. */
  spec:           Record<string, unknown> | null;
  /** 카탈로그 원문 — 약식 생성 실패 시 폴백, bay 토큰 추출용. */
  descriptionRaw: string | null;
  description:    string;
  /** 사용자가 디스크립션을 직접 고쳤는지 — AUTO 재작성 방지. */
  descriptionManual: boolean;
  quantity:       number;
  /** 구성품 판매 단가. 미입력은 null (0 아님). */
  salePrice:      number | null;
  isUnregistered: boolean;
  /**
   * 기본 구성(chassis_base_parts)에서 자동으로 담긴 행인가 — 마이그 174.
   * TRUE 면 `기본` 뱃지 + 수량·소스 잠금, 매입원가 0(섀시 값에 포함).
   * 사람이 직접 담은 행과 섞이지 않게 저장까지 따라간다.
   */
  isBasePart:     boolean;
  // 소스 스냅샷
  sourceType:     'stock' | 'deal_rfq' | 'rfq_history' | null;
  sourceRefId:    string | null;
  sourceLabel:    string | null;
  costAmount:     number | null;
  costCurrency:   string | null;
}

/** 품목 검색 상태 (blank row / 행 재선택 공용). */
/**
 * 품목 등록 폼 — 검색 결과가 없을 때 팝오버 안에서 펼친다.
 *
 * 프로토타입(admin-ux-preview `configuration2` 레지스트레이션)과 같은 규격이다:
 *   카테고리(CHASSIS 제외) · 파트넘버 · 디스크립션 **셋 다 필수**,
 *   에러는 토스트가 아니라 폼 안에 띄운다.
 * CHASSIS 를 빼는 이유 — 섀시는 전용 모달에서만 고른다. 여기서도 고를 수 있게
 * 하면 같은 일을 하는 경로가 둘이 된다.
 */
export interface RowRegisterState {
  open:     boolean;
  category: SpecCategory | '';
  part:     string;
  desc:     string;
  /** 검증 실패 문구. 저장을 눌렀을 때만 채운다. */
  error:    string;
}

export interface RowSearchState {
  /** 검색 중인 행의 key. null 이면 닫힘. */
  rowKey:    string | null;
  query:     string;
  searching: boolean;
  hits:      api.CatalogSearchHit[];
  error:     string;
  /**
   * 앵커(검색 input) 좌표 — 팝오버를 그 아래에 띄운다.
   * `position: fixed` 로 못박아야 표가 아래로 늘어나지 않는다
   * (rAF 보정만으로는 한 프레임 늘어남 — 기존 팝오버들과 같은 규칙).
   */
  x:         number;
  y:         number;
  width:     number;
  /** 결과 없음 → '품목 등록' 폼. */
  register:  RowRegisterState;
}

export function emptyRowRegister(): RowRegisterState {
  return { open: false, category: '', part: '', desc: '', error: '' };
}

/** 최초 Chassis 선택 모달 — 취소로 닫을 수 없다(신규 모드). */
export interface ChassisModalState {
  open:      boolean;
  /** replacement 모드 = 이미 Chassis 가 있는 상태에서 교체. 취소 가능. */
  replace:   boolean;
  query:     string;
  searching: boolean;
  hits:      api.CatalogSearchHit[];
  /** stage 된 선택 — '변경 완료' 누를 때만 반영. */
  pickedPart:      string;
  pickedDesc:      string;
  pickedCondition: 'NEW' | 'USED' | '';
  error:     string;
  // ─── 품목 등록(inline) — 카탈로그에 없는 Chassis 를 임시로 등록 ────
  //   canonical 카탈로그를 바꾸지 않는다. 저장 시 백엔드가 카탈로그 부재를
  //   보고 is_unregistered 를 자동 판정하며 '임시' badge 로 표시된다.
  registerOpen: boolean;
  registerPart: string;
  registerDesc: string;
}

/**
 * 후보(장바구니) — Chassis 선택 후 카테고리별로 묶어 보여준다.
 *
 * 원천은 `chassis_candidate_part`(마이그 151) = 사람이 담은 것이다.
 * QuickSpec 호환 목록은 여기에 나오지 않는다(추천이라 후보 관리 모달 안에만).
 * 후보는 checkbox 가 아니라 `추가` 로 명시적으로 구성에 넣는다.
 * 새 구성은 전체 펼침, 수정 모드는 전체 접힘으로 시작한다.
 */
export interface CandidateGroupState {
  specCategory: SpecCategory;
  count:        number;
  items:        api.CandidateItem[];
  open:         boolean;
}

/**
 * 후보 관리 모달의 pool 표시 상한.
 *
 * pool 은 그 카테고리의 카탈로그 전량(CPU 541건)이라 DOM 을 다 만들지 않는다.
 * 후보 목록 쪽에는 상한이 없다 — 사람이 담은 몇 건뿐이라 전부 그린다.
 */
export const CAND_LIMIT_INITIAL = 20;
export const CAND_LIMIT_STEP    = 50;

/**
 * 이 섀시가 속한 **후보 공유 그룹**(마이그 172).
 *
 * 아래 `CandidateGroupState` 와 헷갈리지 말 것 — 그쪽은 "견적 카테고리별
 * 아코디언"이고, 이쪽은 "후보 장바구니를 같이 쓰는 섀시 SKU 묶음"이다.
 */
export interface CandidateShareGroup {
  label:    string;
  /** 자기 자신 포함. 서버가 1 이면 아예 null 로 접어서 보낸다. */
  skuCount: number;
  members:  string[];
}

export interface CandidatesState {
  /** 후보를 불러온 기준 Chassis 파트넘버. 바뀌면 다시 불러온다. */
  chassisPart: string;
  loading:     boolean;
  error:       string;
  /**
   * CHASSIS 를 제외한 17개 카테고리 전부. 담긴 후보가 없어도 그룹을 만든다 —
   * 후보 관리 진입점이 그룹 헤더의 버튼이라, 빈 카테고리를 빼면 담을 방법이
   * 화면에서 사라진다.
   */
  groups:      CandidateGroupState[];
  /** 후보를 공유하는 SKU 묶음. 단독이거나 미편성이면 null. */
  shareGroup:  CandidateShareGroup | null;
}

export function emptyCandidates(): CandidatesState {
  return { chassisPart: '', loading: false, error: '', groups: [], shareGroup: null };
}

/**
 * 후보 관리 모달 — 카테고리 하나의 장바구니를 편집한다(staged).
 *
 * pool 은 그 카테고리의 카탈로그 전량이고, `checked` 는 파트넘버 Set 으로
 * 들고 있는다. 검색으로 행이 숨어도 체크를 잃지 않아야 하기 때문이다
 * (핸드오프 §4). '전체 선택'은 **보이는 결과만** 대상으로 한다.
 */
export interface CandidateModalState {
  open:         boolean;
  specCategory: SpecCategory | '';
  loading:      boolean;
  saving:       boolean;
  error:        string;
  /** 카탈로그 전량. 서버가 필터링하지 않는다. */
  pool:         api.CandidatePoolItem[];
  /** 상한(5000)을 넘겨 잘렸는가 — 검색으로 좁히라고 안내한다. */
  truncated:    boolean;
  /** 담긴 파트넘버 집합. filter 밖에서도 유지된다. */
  checked:      Set<string>;
  query:        string;
  /** 표시 상한 — pool 이 수천 건이라 DOM 을 다 만들지 않는다. */
  limit:        number;
}

export function emptyCandidateModal(): CandidateModalState {
  return {
    open: false, specCategory: '', loading: false, saving: false, error: '',
    pool: [], truncated: false, checked: new Set(), query: '',
    limit: CAND_LIMIT_INITIAL,
  };
}

/** 검색어로 pool 을 좁힌다. 대소문자 무시 + NFC 정규화 + 토큰 AND. */
export function filterPool(m: CandidateModalState): api.CandidatePoolItem[] {
  const tokens = nfc(m.query).trim().split(/\s+/).filter(Boolean);
  if (tokens.length === 0) return m.pool;
  return m.pool.filter((it) => {
    const hay = nfc(`${it.option_part} ${it.description_raw ?? ''}`);
    return tokens.every((t) => hay.includes(t));
  });
}

/**
 * NFC 정규화 + 소문자.
 *
 * 한글 IME 는 조합 중 NFD(분해형)를 내보내는 구간이 있어, 정규화하지 않으면
 * '메모리' 를 쳐도 NFC 로 저장된 디스크립션과 문자열 비교가 실패한다.
 */
function nfc(s: string): string {
  return s.normalize('NFC').toLowerCase();
}

/**
 * 소스 후보 — 재고(stock) + RFQ 둘뿐이다(2026-07-29 회의).
 *   재고: GET /server-config/stock (파트 목록 일괄)
 *   RFQ : 기존 GET /rfq/deal-sources?deal_id=
 * 파트별로 묶어 드롭다운 옵션을 만든다.
 */
export interface SourcesState {
  loading: boolean;
  error:   string;
  stock:   api.StockCandidate[];
  rfq:     api.RfqCandidate[];
}

export function emptySources(): SourcesState {
  return { loading: false, error: '', stock: [], rfq: [] };
}

/**
 * 기본 구성 관리 모달 — 이 Chassis 모델의 재사용 구성(chassis_base_parts).
 *
 * 후보(장바구니)와 다른 것이다:
 *   후보 = "무엇을 담아뒀나" (고를 거리)
 *   기본 구성 = "무엇을 몇 개 자동으로 넣나" (확정 세트 + 수량)
 *
 * 소스/원가/판매가는 저장하지 않는다 — 시간가변 상거래 값은 딜에서만 관리한다.
 * 완료를 누르면 저장 + 현재 구성 표의 부품을 이 세트로 **대체**한다.
 */
export interface BaseRow {
  key:               string;
  specCategory:      SpecCategory | '';
  optionPart:        string;
  catalogSource:     string | null;
  description:       string;
  descriptionRaw:    string | null;
  /** 수기로 고쳤는가 — description_mode(AUTO/MANUAL_OVERRIDE) 로 저장된다. */
  descriptionManual: boolean;
  /** 서버 1대당 수량(예: FAN 6). 서버 대수는 딜에서 곱한다. */
  quantity:          number;
  isUnregistered:    boolean;
}

export interface BaseModalState {
  open:     boolean;
  loading:  boolean;
  saving:   boolean;
  error:    string;
  /** 모델 키 = Chassis 파트넘버 대문자. */
  modelKey: string;
  /**
   * 편집 중인 컨디션(마이그 173). 모달은 **현재 서버 컨디션 하나만** 편집한다 —
   * 한 모달에서 신품·중고를 같이 만지면 '완료' 가 무엇을 저장하는지 모호해진다.
   * 다른 컨디션을 고치려면 서버 컨디션을 바꾸고 다시 연다.
   */
  condition: 'NEW' | 'USED';
  /** 저장된 기본 구성이 이미 있는가 — 빈 배열 저장(=삭제)과 구분해 안내한다. */
  exists:   boolean;
  items:    BaseRow[];
}

export function emptyBaseModal(): BaseModalState {
  return {
    open: false, loading: false, saving: false, error: '',
    modelKey: '', condition: 'NEW', exists: false, items: [],
  };
}

/**
 * 컨디션을 바꿨을 때 뜨는 '기본 구성 교체' 확인 배너의 상태.
 *
 * 왜 confirm() 이 아닌가: 저장 흐름을 막는 모달 대화상자는 이 화면에서 유일한
 * 예외가 되고(다른 확인은 전부 인라인이다), 자동으로 갈아끼우는 것은 사용자가
 * 방금 담은 부품을 말없이 날린다. 그래서 **비차단 배너 + 명시적 클릭**이다.
 *
 * items 는 배너를 띄우기 전에 이미 받아 둔 것이다 — 눌렀을 때 다시 조회하면
 * 그 사이 응답이 달라지거나 실패해 "눌렀는데 아무 일도 안 난다" 가 생긴다.
 * 저장된 구성이 없으면 배너 자체를 띄우지 않는다(폴백 없음).
 */
export interface BaseSwapPrompt {
  condition: 'NEW' | 'USED';
  items:     api.BasePartsItem[];
}

/**
 * 기본 구성 **조회 자체가 실패**한 상태 (통신 오류·백엔드 오류).
 *
 * "이 섀시에 기본 구성이 없다"(exists=false) 와 반드시 구분한다. 전자는
 * 있는지 없는지 **모르는** 상태라, 그걸 근거로 기존 기본 품목을 지우면
 * 사용자는 이유도 모른 채 구성을 잃는다(되돌릴 방법도 없다).
 * 그래서 이 상태에서는 표를 건드리지 않고, 배너로 알리고 저장을 막는다.
 */
export interface BaseLoadError {
  modelKey:  string;
  condition: 'NEW' | 'USED';
  message:   string;
}

export function newBaseRow(): BaseRow {
  return {
    key: newRowKey(), specCategory: '', optionPart: '', catalogSource: null,
    description: '', descriptionRaw: null, descriptionManual: false,
    quantity: 1, isUnregistered: false,
  };
}

export interface ServerConfigData {
  // ─── 컨텍스트 ───────────────────────────────────────────────────
  mode:            'new' | 'edit';
  dealId:          string;
  dealName:        string;
  /**
   * 구성명의 거래처 자리(2026-08-14). 딜명(`dealName`)과 다른 값이다 —
   * 딜명은 `buildDealName` 규칙상 거래처에 파트넘버·카테고리가 붙은 문자열이라
   * 그대로 제목에 쓰면 `… P23449-B21 HDD 중고 서버 구성` 이 된다.
   *
   * ★ 위 `dealName` 은 이 변경으로 **읽는 곳이 없어졌다**(제목이 유일한
   *   소비자였다). 지우지 않은 것은 딜을 가리키는 안내 문구가 생길 때
   *   다시 필요해서가 아니라, 이번 작업 범위를 제목 규칙에 묶어두기 위해서다.
   *   다음에 이 파일을 손대는 사람이 정리해도 된다.
   */
  customerName:    string;
  configId:        string;
  // configNo 는 두지 않는다 — 제목에서 '구성 N의' 를 뺀 뒤로 읽는 곳이 없다.
  // 구성 번호는 딜 상세의 탭이 보여준다.
  /** edit 모드에서 갱신할 서버 헤더행 id. */
  serverItemId:    string | null;
  /** 낙관적 잠금 기준 — 편집 시작 시점 deal.version. */
  expectedVersion: number | undefined;

  // ─── 서버 헤더 ──────────────────────────────────────────────────
  serverCondition: 'NEW' | 'USED' | '';
  /**
   * 구성명의 섀시 모델 자리 — `chassis.model` 원본(`DL380p Gen8`).
   *
   * 후보 응답(`GET /server-config/candidates` → `chassis.model`)에서 받는다.
   * 수정 모드 첫 페인트에는 섀시 행의 `spec` 이 아직 null 이라(lazy 조회)
   * 이 값이 없으면 제목에서 모델이 통째로 빠진다.
   * 카탈로그에 없는 섀시(임시등록·pre_configured_model)면 후보 조회 자체가
   * 404 라 null 로 남고, 화면은 약식 첫 줄로 폴백한다.
   */
  chassisModel:    string | null;
  // 서버 대수는 여기 없다 — 딜 소유값이라 이 화면의 상태가 아니다 (`CONFIG2-018`).
  /** 번들가. 빈 문자열 = 미입력(null 로 전송). */
  bundlePrice:     string;
  /**
   * 서버 판매 가격을 **사람이 직접 적었는가** (2026-08-21 회의 03:36).
   *
   * FALSE 면 구성품 판매가 합계를 그대로 비춘다(mirroring). TRUE 면 손대지
   * 않고, 합계와 다르면 헤더에 불일치를 표시한다. 약식의 descriptionManual 과
   * 같은 규칙이다 — 사람이 적은 값을 자동 생성이 덮지 않는다.
   *
   * ★ 저장하지 않는다. 화면을 다시 열 때는 저장된 번들가와 구성품 합계의
   *   관계로 되살린다(번들가가 비었거나 합계와 같으면 자동, 다르면 수동).
   *   두 상태는 '다를 때' 를 빼면 동작이 같아 추측이 될 여지가 없고,
   *   다를 때는 저장된 값을 지키는 쪽이 안전하다. 그 차이는 불일치 표시로
   *   화면에 드러나므로 조용히 넘어가지도 않는다.
   */
  bundleManual:    boolean;

  /**
   * 매입단가 환산에 쓰는 환율(KRW/USD). 빈 문자열이면 미입력.
   *
   * ★ **저장하지 않는다.** 화면을 열 때 오늘 환율로 채우고 사람이 그 자리에서
   *   고칠 수 있다 — 견적 모달의 환율 칸과 같은 동작이다.
   *
   *   딜에 박아두지 않는 이유: 매입가는 매입처가 준 확정값이라 고른 시점을
   *   복사할 이유가 있지만(마이그 147), 환율은 그 시점에 확정된 것이 없다.
   *   USD 로 사올 물건의 원화 원가는 결제까지 계속 움직인다. 박아두면 확정되지
   *   않은 것을 확정된 것처럼 기록하게 되고, 그 값을 읽을 곳도 이 화면뿐이다
   *   — 견적은 자기 fx_rate 로 다시 환산한다(quote-math rowCostKrw).
   *
   *   그래서 규칙은 하나다: **저장은 견적만, 표시는 각 화면이 자기 환율로.**
   *   이 화면의 마진은 "이 환율이면 이 정도"라는 참고값이고, 계약 금액을
   *   못박는 것은 견적이다.
   */
  fxRate:          string;

  // ─── 구성 grid ──────────────────────────────────────────────────
  rows:            ConfigRow[];
  rowSearch:       RowSearchState;
  chassisModal:    ChassisModalState;
  candidates:      CandidatesState;
  candidateModal:  CandidateModalState;
  baseModal:       BaseModalState;
  /** 컨디션 변경 후 '그 컨디션의 기본 구성으로 교체할까' 배너. null = 없음. */
  baseSwap:        BaseSwapPrompt | null;
  /** 기본 구성 조회 실패. null = 정상. 있으면 저장이 막힌다. */
  baseLoadError:   BaseLoadError | null;
  sources:         SourcesState;

  /**
   * 후보 행에서 고른 소스(파트넘버 → select value). 선택만 해두고 `추가` 때
   * 구성 행에 함께 반영한다 — 소스를 고르는 즉시 담기면 실수로 담긴다.
   */
  candidateSource: Record<string, string>;

  // ─── 저장 ───────────────────────────────────────────────────────
  saving:          boolean;
  error:           string;
}

export function emptyRowSearch(): RowSearchState {
  return {
    rowKey: null, query: '', searching: false, hits: [], error: '',
    x: 0, y: 0, width: 0, register: emptyRowRegister(),
  };
}

export function emptyChassisModal(): ChassisModalState {
  return {
    open: false, replace: false, query: '', searching: false, hits: [],
    pickedPart: '', pickedDesc: '', pickedCondition: '', error: '',
    registerOpen: false, registerPart: '', registerDesc: '',
  };
}

let rowSeq = 0;
export function newRowKey(): string {
  rowSeq += 1;
  return `r${rowSeq}`;
}

export function blankRow(): ConfigRow {
  return {
    key: newRowKey(), specCategory: '', optionPart: '',
    catalogSource: null, spec: null, descriptionRaw: null, description: '',
    descriptionManual: false, quantity: 1, salePrice: null, isUnregistered: false,
    isBasePart: false,
    sourceType: null, sourceRefId: null, sourceLabel: null,
    costAmount: null, costCurrency: null,
  };
}

/** deal_item 자식행 → grid 행. */
export function rowFromDealItem(it: api.DealItem): ConfigRow {
  return {
    id:             it.id,
    key:            newRowKey(),
    // spec_category 가 비어 있으면 카탈로그 카테고리에서 유도한다.
    //   V2 이전에 만들어진 서버는 이 컬럼이 없어, 그대로 열면 전 행이
    //   "카테고리 지정 필요" 가 되고 CHASSIS 가 없어 편집 자체가 막힌다.
    specCategory:   (it.spec_category
      ?? toSpecCategory(it.catalog_source ?? it.category)
      ?? '') as SpecCategory | '',
    optionPart:     it.option_part ?? '',
    catalogSource:  it.catalog_source ?? null,
    // 저장된 스펙은 없다 — 수량이 바뀌어 약식을 다시 만들어야 할 때 lazy 조회한다.
    spec:           null,
    descriptionRaw: it.description_raw ?? null,
    description:    it.item_description ?? it.description_raw ?? '',
    // 저장된 값을 그대로 읽는다 — 마이그 191.
    //
    //   예전엔 `item_description !== description_raw` 로 추측했다. 그런데
    //   item_description 에는 약식이 들어가고('800W *1EA') description_raw 는
    //   카탈로그 원문이라('HPE 800W Flex Slot Platinum …') 설계상 늘 다르다.
    //   아무도 타이핑한 적 없는 행까지 수기 수정으로 잡혔다(prod 252 중 206).
    //   그러면 applyShorthand·refreshShorthand·buildHeaderSummary 가 첫 줄에서
    //   빠져나가 수정 모드에서 약식이 영영 다시 만들어지지 않는다.
    descriptionManual: it.description_mode === 'MANUAL_OVERRIDE',
    quantity:       it.quantity ?? 1,
    salePrice:      it.sell_unit_price_krw ?? null,
    isUnregistered: it.is_unregistered,
    isBasePart:     it.is_base_part ?? false,
    sourceType:     it.source_type ?? null,
    sourceRefId:    it.source_ref_id ?? null,
    sourceLabel:    it.source_label ?? null,
    costAmount:     it.cost_basis_amount ?? null,
    costCurrency:   it.cost_basis_currency ?? null,
  };
}
